import React, { type ReactNode } from "react"
import {
  Box,
  Divider,
  Flex,
  Stack,
  Text,
} from "@chakra-ui/react"
import AppleWalletPassPreview, { type AppleWalletPassPreviewProps } from "./AppleWalletPassPreview"
import { APPLE_BACK_SECTION_LINK_TYPES } from "./walletPassBuilderTypes"
import {
  PASS_BUILDER_PREVIEW_BACK_COLOR_BODY,
  PASS_BUILDER_PREVIEW_BACK_COLOR_DIVIDER,
  PASS_BUILDER_PREVIEW_BACK_COLOR_LINK,
  PASS_BUILDER_PREVIEW_BACK_COLOR_PAGE,
  PASS_BUILDER_PREVIEW_BACK_COLOR_TEXT,
  PASS_BUILDER_PREVIEW_BACK_FONT_BODY_REF,
  PASS_BUILDER_PREVIEW_BACK_FONT_ROW_REF,
  PASS_BUILDER_PREVIEW_BACK_FONT_TITLE_REF,
  PASS_BUILDER_PREVIEW_BACK_INFO_GAP_AFTER_DIVIDER_REF,
  PASS_BUILDER_PREVIEW_BACK_INFO_GAP_BEFORE_DIVIDER_REF,
  PASS_BUILDER_PREVIEW_BACK_INFO_LABEL_BODY_GAP_REF,
  PASS_BUILDER_PREVIEW_BACK_INFO_PANEL_PB_REF,
  PASS_BUILDER_PREVIEW_BACK_INFO_PANEL_PT_REF,
  PASS_BUILDER_PREVIEW_BACK_PANEL_GAP_REF,
  PASS_BUILDER_PREVIEW_BACK_PANEL_INSET_REF,
  PASS_BUILDER_PREVIEW_BACK_PANEL_PADDING_REF,
  PASS_BUILDER_PREVIEW_BACK_PANEL_RADIUS_REF,
  PASS_BUILDER_PREVIEW_BACK_PANEL_WIDTH_REF,
  PASS_BUILDER_PREVIEW_BACK_THUMB_HEIGHT,
  PASS_BUILDER_PREVIEW_BACK_THUMB_SCALE,
  PASS_BUILDER_PREVIEW_BACK_THUMB_TOP_REF,
  PASS_BUILDER_PREVIEW_BACK_THUMB_WIDTH,
  PASS_BUILDER_PREVIEW_BACK_TITLE_AFTER_THUMB_REF,
  PASS_BUILDER_PREVIEW_BACK_TITLE_TO_PANEL_REF,
  PASS_BUILDER_PREVIEW_PASS_BASE_WIDTH,
} from "./passBuilderTokens"

type BackSection = {
  label: string
  value: string
  type?: string
}

type Props = AppleWalletPassPreviewProps & {
  sections?: BackSection[]
  /** The event's own name. iOS titles the back with the pass description, not
   * with any front-of-pass field — those blank out under a banner or logo. */
  eventTitle?: string
}

/** Sketch `Passbook Back - Custom` card (375×550) */
const REF_WIDTH = 375
const scale = PASS_BUILDER_PREVIEW_PASS_BASE_WIDTH / REF_WIDTH
const px = (value: number) => `${Math.round(value * scale)}px`
const py = (value: number) => `${Math.round(value * scale)}px`

const PREVIEW_HEIGHT = Math.round(550 * scale)

const FONT_FAMILY = "system-ui, -apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Segoe UI', sans-serif"

const PANEL_WIDTH = px(PASS_BUILDER_PREVIEW_BACK_PANEL_WIDTH_REF)
const PANEL_INSET = px(PASS_BUILDER_PREVIEW_BACK_PANEL_INSET_REF)
const PANEL_RADIUS = px(PASS_BUILDER_PREVIEW_BACK_PANEL_RADIUS_REF)
const PANEL_PADDING = px(PASS_BUILDER_PREVIEW_BACK_PANEL_PADDING_REF)
const PANEL_GAP = py(PASS_BUILDER_PREVIEW_BACK_PANEL_GAP_REF)

const DIVIDER = <Divider borderColor={PASS_BUILDER_PREVIEW_BACK_COLOR_DIVIDER} />

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const URL_RE = /^(https?:\/\/|www\.)\S+$/i
const PHONE_RE = /^\+?[\d\s().-]{7,}$/

/** iOS runs data detectors over every back field, so a value that looks like an
 * email, phone number or URL is tappable whatever the row was typed as. */
const isDetectedLink = (value: string) => (
  EMAIL_RE.test(value.trim()) || URL_RE.test(value.trim()) || PHONE_RE.test(value.trim())
)

const isLinkRow = (value: string, type?: string) => (
  (type && APPLE_BACK_SECTION_LINK_TYPES.has(type)) || isDetectedLink(value)
)

const sectionValueColor = (value: string, type?: string) => {
  if (isLinkRow(value, type)) return PASS_BUILDER_PREVIEW_BACK_COLOR_LINK
  if (type === "location") return PASS_BUILDER_PREVIEW_BACK_COLOR_BODY

  return PASS_BUILDER_PREVIEW_BACK_COLOR_TEXT
}

const PassBackPanel = ({ children }: { children: ReactNode }) => (
  <Box
    w={PANEL_WIDTH}
    mx={PANEL_INSET}
    bg="white"
    borderRadius={PANEL_RADIUS}
    overflow="hidden"
  >
    {children}
  </Box>
)

const AppleWalletPassBackPreview = ({
  sections = [],
  eventTitle,
  ...frontProps
}: Props) => {
  const { brandName, eventName } = frontProps
  const title = eventTitle || [brandName, eventName].filter(Boolean).join(" ")

  return (
    <Box
      w={`${PASS_BUILDER_PREVIEW_PASS_BASE_WIDTH}px`}
      minH={`${PREVIEW_HEIGHT}px`}
      bg={PASS_BUILDER_PREVIEW_BACK_COLOR_PAGE}
      border="1px solid"
      borderColor="#DBDBDB"
      overflow="hidden"
      fontFamily={FONT_FAMILY}
      display="flex"
      flexDirection="column"
      pb={PANEL_GAP}
    >
      <Flex pt={py(PASS_BUILDER_PREVIEW_BACK_THUMB_TOP_REF)} justify="center" flexShrink={0} w="full">
        <Box
          w={`${PASS_BUILDER_PREVIEW_BACK_THUMB_WIDTH}px`}
          h={`${PASS_BUILDER_PREVIEW_BACK_THUMB_HEIGHT}px`}
          overflow="hidden"
        >
          <Box
            transform={`scale(${PASS_BUILDER_PREVIEW_BACK_THUMB_SCALE})`}
            transformOrigin="top left"
          >
            <AppleWalletPassPreview {...frontProps} showQr={false} />
          </Box>
        </Box>
      </Flex>

      <Text
        px={px(16)}
        pt={py(PASS_BUILDER_PREVIEW_BACK_TITLE_AFTER_THUMB_REF)}
        pb={py(PASS_BUILDER_PREVIEW_BACK_TITLE_TO_PANEL_REF)}
        fontSize={px(PASS_BUILDER_PREVIEW_BACK_FONT_TITLE_REF)}
        fontWeight="700"
        lineHeight={1.15}
        color={PASS_BUILDER_PREVIEW_BACK_COLOR_TEXT}
        textAlign="center"
        noOfLines={3}
        flexShrink={0}
      >
        {title}
      </Text>

      <Stack spacing={PANEL_GAP} flex={1} w="full">
        {sections.length > 0 && (
          <PassBackPanel>
            <Stack
              spacing={0}
              divider={DIVIDER}
              px={PANEL_PADDING}
              pt={px(PASS_BUILDER_PREVIEW_BACK_INFO_PANEL_PT_REF)}
              pb={px(PASS_BUILDER_PREVIEW_BACK_INFO_PANEL_PB_REF)}
            >
              {sections.map((section, index) => {
                const isTextBlock = section.type === "text" && !isDetectedLink(section.value)

                return (
                  <Box
                    key={index}
                    pt={py(PASS_BUILDER_PREVIEW_BACK_INFO_GAP_AFTER_DIVIDER_REF)}
                    pb={py(PASS_BUILDER_PREVIEW_BACK_INFO_GAP_BEFORE_DIVIDER_REF)}
                  >
                    {isTextBlock ? (
                      <>
                        {section.label && (
                          <Text
                            fontSize={px(PASS_BUILDER_PREVIEW_BACK_FONT_ROW_REF)}
                            fontWeight="600"
                            color={PASS_BUILDER_PREVIEW_BACK_COLOR_TEXT}
                            mb={py(PASS_BUILDER_PREVIEW_BACK_INFO_LABEL_BODY_GAP_REF)}
                          >
                            {section.label}
                          </Text>
                        )}
                        {section.value && (
                          <Text
                            fontSize={px(PASS_BUILDER_PREVIEW_BACK_FONT_BODY_REF)}
                            lineHeight={1.45}
                            fontWeight="400"
                            color={PASS_BUILDER_PREVIEW_BACK_COLOR_BODY}
                            whiteSpace="pre-wrap"
                          >
                            {section.value}
                          </Text>
                        )}
                      </>
                    ) : (
                      <Flex justify="space-between" align="flex-start" gap={3}>
                        <Text
                          fontSize={px(PASS_BUILDER_PREVIEW_BACK_FONT_ROW_REF)}
                          fontWeight="400"
                          color={PASS_BUILDER_PREVIEW_BACK_COLOR_TEXT}
                          flexShrink={0}
                        >
                          {section.label}
                        </Text>
                        <Text
                          fontSize={px(PASS_BUILDER_PREVIEW_BACK_FONT_ROW_REF)}
                          fontWeight="400"
                          color={sectionValueColor(section.value, section.type)}
                          textAlign="right"
                          noOfLines={2}
                          textDecoration={section.type === "location" ? "underline" : undefined}
                        >
                          {section.value}
                        </Text>
                      </Flex>
                    )}
                  </Box>
                )
              })}
            </Stack>
          </PassBackPanel>
        )}
      </Stack>
    </Box>
  )
}

export default AppleWalletPassBackPreview
